import * as THREE from 'three'
import clearCanvasOverlay from './clearCanvasOverlay'
import getMaterialTexture from '../materialTextures'
import { materialColorToHex } from '../materialColor'

// This interface defines the properties we access on the shader object.
// This is a workaround because 'Shader' is not an exported type from 'three'.
interface MinimalShader {
  uniforms: { [uniform: string]: THREE.IUniform<any> }
  vertexShader: string
  fragmentShader: string
}

// Define types for custom userData
interface CustomMeshUserData {
  overlayTexture?: THREE.CanvasTexture
  overlayTextureBack?: THREE.CanvasTexture
  size?: THREE.Vector2
  originalMaterial?: THREE.MeshStandardMaterial
}

const textureLoader = new THREE.TextureLoader()
const metalnessMap = textureLoader.load('/assets/images/textures/noise.png')

// Colour a material lays over the engraved mark.
//
// The tint carries hue only — brightness stays the engraving tint's job — so
// these are the 2D preview colours normalised by their brightest channel. Only
// materials that actually tint carry an entry; everything else multiplies by
// white, a no-op. Kept in step with the customizer's materialConfigs.
const materialTints: Record<string, number> = {
  gold: 0xffbb2f,
  wood_burn: 0xdd5c1a,
  anodized: 0xddddff,
}

// Frosted finishes read as grain rather than colour, so they sample a tile the
// way a custom pattern does. These reuse the same procedural generators the 2D
// preview draws with, which keeps the two previews showing one material.
const proceduralGrainMaterials = new Set(['sandblast', 'glass_etch'])

const proceduralMaterialMaps = new Map<string, THREE.CanvasTexture>()

const getProceduralMaterialMap = (materialType: string): THREE.Texture | null => {
  const cached = proceduralMaterialMaps.get(materialType)
  if (cached) return cached

  const tile = getMaterialTexture(materialType)
  if (!tile) return null

  const map = new THREE.CanvasTexture(tile)
  map.wrapS = THREE.RepeatWrapping
  map.wrapT = THREE.RepeatWrapping
  map.needsUpdate = true
  proceduralMaterialMaps.set(materialType, map)
  return map
}

// Custom material patterns are per-section uploads, cached by url so a tile
// shared by several sections is only fetched and uploaded to the GPU once.
const customMaterialMaps = new Map<string, THREE.Texture>()

const getCustomMaterialMap = (url: string | null): THREE.Texture | null => {
  if (!url) return null
  const cached = customMaterialMaps.get(url)
  if (cached) return cached

  const map = textureLoader.load(
    url,
    () => (window as any).threeViewer?.allowRendering?.(),
    undefined,
    () => console.warn(`Failed to load custom material texture: ${url}`)
  )
  map.wrapS = THREE.RepeatWrapping
  map.wrapT = THREE.RepeatWrapping
  customMaterialMaps.set(url, map)
  return map
}

// Map projection type string to shader float (float for reliable WebGL compatibility)
const projectionTypeMap: Record<string, number> = {
  cylindrical: 0.0,
  planar: 1.0,
  box: 2.0,
  none: 3.0,
}

const applyCanvasOverlayWithShader = async (name: string): Promise<void> => {
  const mesh = (window as any).threeViewer?.product?.getObjectByName(name) as
    | THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>
    | undefined

  const canvas = document.querySelector<HTMLCanvasElement>(`#targetObjectCanvas-${name}`)
  const renderer = (window as any).threeViewer?.renderer

  if (!mesh || !renderer) {
    console.warn(`Mesh, canvas, or renderer not found, or material is not MeshStandardMaterial for: ${name}`)
    return
  }

  if (!canvas) {
    clearCanvasOverlay(name)
    return
  }

  const userData = mesh.userData as CustomMeshUserData
  const canvasBack = document.querySelector<HTMLCanvasElement>(`#targetObjectCanvas-${name}-back`)
  const hasBack = !!canvasBack

  // Read UV projection type from threeViewer
  const uvProjection: string = (window as any).threeViewer?.uvProjection || 'cylindrical'
  const projType = projectionTypeMap[uvProjection] ?? 3.0
  const expectedCacheKey = `overlay_${name}_${projType}_${hasBack ? 'dual' : 'single'}`

  // Resolved before the fast path so a material change refreshes the uniforms
  // without waiting for a shader recompile.
  const sectionMaterial = (window as any).threeViewer?.sectionMaterials?.[name]
  const materialType: string = sectionMaterial?.type || 'steel'
  // Direct Color prints the artwork's own RGB instead of repainting it with a
  // material, so no tint and no grain apply.
  const isColorMode = materialType === 'color'
  // A custom material's picked colour takes the place of a built-in's tint.
  const customTint = materialType === 'custom' ? materialColorToHex(sectionMaterial?.color) : null
  const materialTint = new THREE.Color(customTint ?? materialTints[materialType] ?? 0xffffff)
  const materialPatternMap = isColorMode
    ? null
    : materialType === 'custom'
    ? getCustomMaterialMap(sectionMaterial?.file || null)
    : proceduralGrainMaterials.has(materialType)
    ? getProceduralMaterialMap(materialType)
    : null
  // Procedural tiles are 256; uploaded patterns are squared to 512 by
  // resizeToTile. `image` is undefined until a loaded texture is ready, so fall
  // back to the size that kind of material is authored at.
  const materialTileSize = materialPatternMap?.image?.width || (materialType === 'custom' ? 512.0 : 256.0)

  // Fast path: if we already have our custom material with the same shader configuration,
  // just update the canvas textures without cloning material or recompiling shaders
  const existingShader = (mesh.material as any)?.userData?.shader as MinimalShader | undefined
  const existingCacheKey = (mesh.material as any)?.customProgramCacheKey?.()
  if (existingShader && existingCacheKey === expectedCacheKey && userData.overlayTexture) {
    // Update front texture in-place
    userData.overlayTexture.image = canvas
    userData.overlayTexture.needsUpdate = true

    // Update back texture in-place
    if (canvasBack && userData.overlayTextureBack) {
      userData.overlayTextureBack.image = canvasBack
      userData.overlayTextureBack.needsUpdate = true
    }

    // Update size uniform if canvas dimensions changed
    if (userData.size) {
      userData.size.x = canvas.width
      userData.size.y = canvas.height
    }

    // Switching material after the shader compiled has to refresh these, or the
    // mesh keeps the material it first rendered with.
    const u = existingShader.uniforms
    if (u.materialTint) u.materialTint.value = materialTint
    if (u.useColorMode) u.useColorMode.value = isColorMode ? 1.0 : 0.0
    if (u.materialPatternMap) u.materialPatternMap.value = materialPatternMap
    if (u.useMaterialPattern) u.useMaterialPattern.value = materialPatternMap ? 1.0 : 0.0
    if (u.materialPatternTiling) {
      u.materialPatternTiling.value.set(canvas.width / materialTileSize, canvas.height / materialTileSize)
    }

    return
  }

  // Full path: first time setup or configuration changed — create textures, clone material, compile shader
  let overlayTexture = userData.overlayTexture
  if (overlayTexture?.dispose) overlayTexture?.dispose()

  // Create or update the canvas texture
  overlayTexture = new THREE.CanvasTexture(canvas)
  overlayTexture.colorSpace = THREE.SRGBColorSpace
  overlayTexture.wrapS = THREE.ClampToEdgeWrapping
  overlayTexture.wrapT = THREE.ClampToEdgeWrapping
  overlayTexture.flipY = false

  overlayTexture.minFilter = THREE.LinearFilter
  overlayTexture.magFilter = THREE.LinearFilter
  overlayTexture.anisotropy = renderer.capabilities.getMaxAnisotropy() ?? 8
  overlayTexture.generateMipmaps = false

  userData.overlayTexture = overlayTexture

  // Create back-face overlay texture if a back canvas exists
  let overlayTextureBack = userData.overlayTextureBack
  if (overlayTextureBack?.dispose) overlayTextureBack.dispose()

  if (canvasBack) {
    overlayTextureBack = new THREE.CanvasTexture(canvasBack)
    overlayTextureBack.colorSpace = THREE.SRGBColorSpace
    overlayTextureBack.wrapS = THREE.ClampToEdgeWrapping
    overlayTextureBack.wrapT = THREE.ClampToEdgeWrapping
    overlayTextureBack.flipY = false
    overlayTextureBack.minFilter = THREE.LinearFilter
    overlayTextureBack.magFilter = THREE.LinearFilter
    overlayTextureBack.anisotropy = renderer.capabilities.getMaxAnisotropy() ?? 8
    overlayTextureBack.generateMipmaps = false
    overlayTextureBack.needsUpdate = true
  } else {
    overlayTextureBack = undefined
  }
  userData.overlayTextureBack = overlayTextureBack

  // Ensure userData properties exist
  if (!userData.size) {
    userData.size = new THREE.Vector2()
  }

  userData.size.x = canvas.width
  userData.size.y = canvas.height

  overlayTexture.needsUpdate = true

  mesh.geometry.computeBoundingBox()

  // Store the original material if not already stored or if it's invalid
  if (!userData.originalMaterial || typeof userData.originalMaterial.clone !== 'function') {
    userData.originalMaterial = mesh.material.clone()
  }

  const baseMaterial = userData.originalMaterial

  // Clone the material to apply custom shaders (preserve original side setting — DoubleSide is
  // required for thin/flat geometry like crystal panels; mutating baseMaterial would corrupt the
  // stored original for future restores via clearCanvasOverlay)
  const material = baseMaterial.clone() as THREE.MeshStandardMaterial

  // Force the overlay material into a known-good shader configuration.
  // Physical/glass materials (transmission, clearcoat, sheen, iridescence) add extra shader
  // code paths that conflict with our onBeforeCompile patches.  The overlay is a temporary
  // section-editing visual indicator, so PBR glass effects are not needed here.
  const mat = material as any
  if (mat.transmission !== undefined) mat.transmission = 0
  if (mat.thickness !== undefined) mat.thickness = 0
  if (mat.clearcoat !== undefined) mat.clearcoat = 0
  if (mat.sheen !== undefined) mat.sheen = 0
  if (mat.iridescence !== undefined) mat.iridescence = 0
  material.transparent = false
  material.opacity = 1
  material.depthWrite = true
  material.alphaTest = 0

  const bbox = mesh.geometry.boundingBox
  if (!bbox) {
    console.error(`Bounding box not computed for mesh: ${name}`)
    return
  }

  const cylinderHeight = bbox.max.y - bbox.min.y

  // Geometry XZ axis center — the cylindrical axis is not guaranteed to sit at the
  // local origin (parent-group transforms are not baked into the geometry), so the
  // angle must be measured around this center, matching the shader below.
  const axisCenterX = (bbox.min.x + bbox.max.x) * 0.5
  const axisCenterZ = (bbox.min.z + bbox.max.z) * 0.5

  // Compute the logo_main angle in this mesh's local coordinate system so the
  // cylindrical UV u=0.5 aligns with the visual front regardless of intermediate
  // group transforms between the product root and this mesh.
  let logoAngleOffset = 0.0
  const logo = (window as any).threeViewer?.product?.getObjectByName('logo_main') as THREE.Object3D | undefined
  if (logo) {
    // Get the logo center in world space
    const logoWorldPos = new THREE.Vector3()
    logo.updateWorldMatrix(true, true)
    new THREE.Box3().setFromObject(logo).getCenter(logoWorldPos)

    // Transform logo world position into the mesh's local coordinate system
    mesh.updateWorldMatrix(true, true)
    const logoLocal = mesh.worldToLocal(logoWorldPos)

    // The angle of the logo in the mesh's local XZ plane, measured around the axis center
    const logoTheta = Math.atan2(logoLocal.x - axisCenterX, logoLocal.z - axisCenterZ)
    // We want the logo to map to u=0.5 (theta_adjusted=0), so offset = -logoTheta
    logoAngleOffset = -logoTheta
  }

  // Unique cache key so Three.js compiles our custom shader instead of reusing a cached standard program
  material.customProgramCacheKey = () => expectedCacheKey

  material.onBeforeCompile = (shader: MinimalShader) => {
    // Uniforms
    shader.uniforms.overlayMap = { value: overlayTexture }
    shader.uniforms.cylinderHeight = { value: cylinderHeight }
    shader.uniforms.engraveDarkness = { value: 0.25 }
    shader.uniforms.textureSize = { value: userData.size }
    shader.uniforms.bboxMinY = { value: bbox.min.y }
    shader.uniforms.steelMetalnessMap = { value: metalnessMap }
    shader.uniforms.overlayTint = {
      value: (window as any).threeViewer?.overlyTint ?? new THREE.Color().setHSL(0, 0, 0.5),
    }

    // Material appearance uniforms
    shader.uniforms.materialTint = { value: materialTint }
    shader.uniforms.useColorMode = { value: isColorMode ? 1.0 : 0.0 }
    shader.uniforms.materialPatternMap = { value: materialPatternMap }
    shader.uniforms.useMaterialPattern = { value: materialPatternMap ? 1.0 : 0.0 }
    shader.uniforms.materialPatternTiling = {
      value: new THREE.Vector2(canvas.width / materialTileSize, canvas.height / materialTileSize),
    }

    // Back-face overlay uniforms
    shader.uniforms.overlayMapBack = { value: overlayTextureBack || null }
    shader.uniforms.hasBackOverlay = { value: hasBack ? 1.0 : 0.0 }

    // UV projection uniforms — use vec3 for bbox (matches StepToGLB pattern)
    shader.uniforms.uvProjectionType = { value: projType }
    shader.uniforms.uBboxMin = { value: new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.min.z) }
    shader.uniforms.uBboxMax = { value: new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.max.z) }

    // Logo angle offset so cylindrical UV u=0.5 aligns with logo_main front
    shader.uniforms.logoAngleOffset = { value: logoAngleOffset }

    // Store shader reference on material for later uniform updates
    ;(material as any).userData = (material as any).userData || {}
    ;(material as any).userData.shader = shader

    // 1. DECLARE varyings at the top level of the vertex shader
    shader.vertexShader = shader.vertexShader.replace(
      `#include <common>`,
      `
	        #include <common>
	        varying vec3 vOverlayLocalPos;
	        varying vec3 vLocalNormal;
	        varying vec3 vOverlayWorldPos;
	        varying vec3 vOverlayWorldNormal;
	        varying vec2 vOriginalUv;
		`
    )

    // 2. ASSIGN values to the varyings inside the main shader body
    // Guard uv access with USE_UV — when the material has no textures,
    // Three.js does not declare the uv attribute and referencing it crashes the shader.
    shader.vertexShader = shader.vertexShader.replace(
      `#include <worldpos_vertex>`,
      `
	        #include <worldpos_vertex>
	        vOverlayLocalPos = position;
	        vLocalNormal = normal;
	        vOverlayWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
	        vOverlayWorldNormal = normalize(mat3(modelMatrix) * normal);
	        #if defined(USE_UV) || defined(USE_ANISOTROPY)
	            vOriginalUv = uv;
	        #else
	            vOriginalUv = vec2(0.0, 0.0);
	        #endif
		`
    )

    // Fragment Shader - declare uniforms, varyings, and shared UV functions
    shader.fragmentShader = shader.fragmentShader.replace(
      `#include <common>`,
      `
			#include <common>
			uniform sampler2D overlayMap;
			uniform float cylinderHeight;
			uniform float engraveDarkness;

			uniform vec2 textureSize;
			uniform float bboxMinY;
			varying vec3 vOverlayLocalPos;
			varying vec3 vLocalNormal;
			varying vec3 vOverlayWorldPos;
			varying vec3 vOverlayWorldNormal;
			varying vec2 vOriginalUv;

			uniform sampler2D steelMetalnessMap;
			uniform vec3 overlayTint;

			// Material appearance
			uniform vec3 materialTint;
			uniform float useColorMode;
			uniform sampler2D materialPatternMap;
			uniform float useMaterialPattern;
			uniform vec2 materialPatternTiling;

			// Back-face overlay
			uniform sampler2D overlayMapBack;
			uniform float hasBackOverlay;

			// UV projection uniforms (float for WebGL compat, vec3 for bbox like StepToGLB)
			uniform float uvProjectionType; // 0.0=cylindrical, 1.0=planar, 2.0=box, 3.0=none
			uniform vec3 uBboxMin;
			uniform vec3 uBboxMax;

			// Logo angle offset to align cylindrical UV u=0.5 with logo_main front
			uniform float logoAngleOffset;

			// Shared function: compute overlay UV based on projection type
			vec2 computeOverlayUV(vec3 pos, vec3 norm, float startY, float mapHeight) {
				vec3 size = uBboxMax - uBboxMin;

				if (uvProjectionType < 0.5) {
					// Cylindrical: atan2 around Y axis, offset so u=0.5 aligns with logo front.
					// Center on the geometry's XZ axis — the bottle's cylindrical axis is not
					// guaranteed to sit at the local origin (parent-group transforms are not baked
					// into the geometry), and an off-center axis makes the angle mapping non-linear,
					// rendering equal-width sections at different widths front vs back.
					vec2 axisCenter = vec2((uBboxMin.x + uBboxMax.x) * 0.5, (uBboxMin.z + uBboxMax.z) * 0.5);
					float theta = atan(pos.x - axisCenter.x, pos.z - axisCenter.y) + logoAngleOffset;
					float u = fract((theta + 3.14159265) / (2.0 * 3.14159265));
					float v = (pos.y - startY) / mapHeight;
					return vec2(u, 1.0 - v);
				} else if (uvProjectionType < 1.5) {
					// Planar: front-facing X/Y projection
					float u = (pos.x - uBboxMin.x) / max(size.x, 0.001);
					float v = (pos.y - startY) / mapHeight;
					return vec2(u, 1.0 - v);
				} else if (uvProjectionType < 2.5) {
					// Box: normal-based face selection
					vec3 absNorm = abs(norm);
					float u, v;
					if (absNorm.x >= absNorm.y && absNorm.x >= absNorm.z) {
						u = (pos.z - uBboxMin.z) / max(size.z, 0.001);
						v = (pos.y - startY) / mapHeight;
					} else if (absNorm.y >= absNorm.x && absNorm.y >= absNorm.z) {
						u = (pos.x - uBboxMin.x) / max(size.x, 0.001);
						v = (pos.z - uBboxMin.z) / max(size.z, 0.001);
					} else {
						u = (pos.x - uBboxMin.x) / max(size.x, 0.001);
						v = (pos.y - startY) / mapHeight;
					}
					return vec2(u, 1.0 - v);
				} else {
					// None: use original mesh UVs from the GLB
					return vOriginalUv;
				}
			}

			// Shared function: compute surface mask based on projection type
			float computeVerticalMask(vec3 norm) {
				if (uvProjectionType < 0.5) {
					// Cylindrical: mask top/bottom to avoid pole distortion
					return smoothstep(0.98, 0.7, abs(norm.y));
				}
				// Planar, box, none: apply on all faces
				return 1.0;
			}
    	`
    )

    // Normal Map replacement - use shared UV function
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <normal_fragment_maps>',
      `
      #include <normal_fragment_maps>

      float normalVertMask = computeVerticalMask(vLocalNormal);
      if (normalVertMask > 0.0) {
          float mapHeight_normal = cylinderHeight;
          if (mapHeight_normal > 0.0) {
             vec3 pos_for_uv = vOverlayLocalPos;
             float startY_normal = bboxMinY;

             if (pos_for_uv.y >= startY_normal && pos_for_uv.y <= startY_normal + mapHeight_normal) {
                vec2 vUv = computeOverlayUV(pos_for_uv, vLocalNormal, startY_normal, mapHeight_normal);

                float overlayAlphaCenter = texture2D(overlayMap, vUv).a;
                if (overlayAlphaCenter > 0.0) {
                   vec2 texelSize = vec2(1.0 / textureSize.x, 1.0 / textureSize.y);
                   float alphaN = texture2D(overlayMap, vUv + vec2(0.0, texelSize.y)).a;
                   float alphaS = texture2D(overlayMap, vUv - vec2(0.0, texelSize.y)).a;
                   float alphaE = texture2D(overlayMap, vUv + vec2(texelSize.x, 0.0)).a;
                   float alphaW = texture2D(overlayMap, vUv - vec2(texelSize.x, 0.0)).a;
                   vec3 grad = vec3((alphaE - alphaW), (alphaN - alphaS), 0.0);
                   vec3 disturbance = vec3(grad.x, grad.y, 0.0);
                }
             }
          }
      }
    `
    )

    // Map fragment replacement - use shared UV function
    shader.fragmentShader = shader.fragmentShader.replace(
      `#include <map_fragment>`,
      `
    float overlayAlpha = 0.0;

    // First apply the base texture map (if it exists)
    #ifdef USE_MAP
        vec4 texelColor = texture2D( map, vMapUv );
        diffuseColor *= texelColor;
    #endif

    // Compute surface mask based on projection type
    float verticalMask = computeVerticalMask(vLocalNormal);

    // Camera-facing check: prevents overlay bleeding through transparent materials
    vec3 viewDir = normalize(cameraPosition - vOverlayWorldPos);
    float facingCamera = dot(vOverlayWorldNormal, viewDir);

    // Determine front/back face using local normals (rotation-independent)
    bool isFrontFace = vLocalNormal.z > 0.0;
    bool isBackFace = vLocalNormal.z < 0.0;

    if (facingCamera > 0.0 && verticalMask > 0.0) {
        float mapHeight = cylinderHeight;
        if (mapHeight > 0.0) {
            vec3 pos = vOverlayLocalPos;
            float startY = bboxMinY;

            if (pos.y >= startY && pos.y <= startY + mapHeight) {
                vec2 vUv = computeOverlayUV(pos, vLocalNormal, startY, mapHeight);

                vec4 overlay = vec4(0.0);
                if (uvProjectionType < 0.5) {
                    // Cylindrical: normals are radial, no front/back distinction — sample from front overlay
                    overlay = texture2D(overlayMap, vUv);
                } else if (isFrontFace) {
                    overlay = texture2D(overlayMap, vUv);
                } else if (isBackFace && hasBackOverlay > 0.5) {
                    // Mirror U for back face to prevent reversed text (matches StepToGLB)
                    overlay = texture2D(overlayMapBack, vec2(1.0 - vUv.x, vUv.y));
                }

                // Broaden the alpha test slightly
                float a = (overlay.a > 0.1 ? overlay.a : 0.0) * verticalMask;

                // Colour mode prints the artwork's own RGB. Otherwise the engraving
                // tint carries greyscale brightness and the material supplies hue
                // (gold) or a tiled pattern (custom).
                vec3 colored = useColorMode > 0.5 ? overlay.rgb : overlayTint * materialTint;

                if (useMaterialPattern > 0.5 && a > 0.0) {
                    colored = colored * texture2D(materialPatternMap, vUv * materialPatternTiling).rgb;
                }

                // Mix the colors using our new smooth mask - apply overlay ON TOP of the texture
                diffuseColor.rgb = mix(diffuseColor.rgb, colored, a);

                overlayAlpha = a;
            }
        }
    }
`
    )

    // Metalness map replacement - use shared UV function
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <metalnessmap_fragment>',
      `#include <metalnessmap_fragment>
    if (overlayAlpha > 0.0 && computeVerticalMask(vLocalNormal) > 0.0) {
        float h = cylinderHeight;
        float y0 = bboxMinY;
        vec3 p = vOverlayLocalPos;
        if (p.y >= y0 && p.y <= y0 + h) {
            vec2 mUv = computeOverlayUV(p, vLocalNormal, y0, h);
            float steelM = texture2D(steelMetalnessMap, mUv).r;
            metalnessFactor = mix(metalnessFactor, steelM, overlayAlpha * 0.8);
        }
    }
`
    )

    // Roughness map replacement - use shared mask function
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <roughnessmap_fragment>',
      `#include <roughnessmap_fragment>
				if (overlayAlpha > 0.0 && computeVerticalMask(vLocalNormal) > 0.0) {
            		roughnessFactor = mix(roughnessFactor, 0.15, overlayAlpha);
				}
		`
    )
  }

  material.needsUpdate = true
  mesh.material = material
}

export default applyCanvasOverlayWithShader
